import { MouseEventHandler, SyntheticEvent, useEffect, useRef } from 'react'

type Props = {
    className?: string
    fps?: number
    draw?: (canvas: HTMLCanvasElement, context: CanvasRenderingContext2D, drawCount: number) => void
    onClick?: (e: SyntheticEvent<HTMLCanvasElement, Event>) => void
    onMouseUp?: (e: SyntheticEvent<HTMLCanvasElement, MouseEvent>) => void
    onMouseDown?: (e: SyntheticEvent<HTMLCanvasElement, MouseEvent>) => void
    onMouseOut?: (e: SyntheticEvent<HTMLCanvasElement, MouseEvent>) => void
    onMouseMove?: MouseEventHandler<HTMLCanvasElement> | undefined
}

const CreationToolsCanvas = ({
    className,
    draw,
    fps,
    onClick,
    onMouseUp,
    onMouseDown,
    onMouseOut,
    onMouseMove,
}: Props) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const contextRef = useRef<CanvasRenderingContext2D | null>(null)
    const requestRef = useRef<number>(0)
    const lastDrawRef = useRef<number>(0)
    const drawCountRef = useRef<number>(0)

    const onDraw = () => {
        if (!canvasRef.current || !contextRef.current) return

        const now = Date.now()

        if (fps && now - lastDrawRef.current >= 1000 / fps) {
            lastDrawRef.current = now
            draw?.(canvasRef.current, contextRef.current, drawCountRef.current)
            drawCountRef.current++
        } else if (!fps) {
            draw?.(canvasRef.current, contextRef.current, drawCountRef.current)
            drawCountRef.current++
        }

        requestRef.current = requestAnimationFrame(onDraw)
    }

    useEffect(() => {
        if (!canvasRef.current) return

        lastDrawRef.current = Date.now()
        contextRef.current = canvasRef.current.getContext('2d')
        requestRef.current = requestAnimationFrame(onDraw)

        return () => {
            cancelAnimationFrame(requestRef.current)
        }
    }, [canvasRef, draw])

    return (
        <canvas
            className={className}
            ref={canvasRef}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
            onMouseOut={onMouseOut}
        />
    )
}

export default CreationToolsCanvas
